import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import SqlChatWidget from "./SqlChatWidget";
import { useLocation } from "react-router-dom";

type ChatSource = {
  filename?: string;
  similarity?: number;
};

type ChatMessage = {
  type: "user" | "assistant";
  text: string;
  sources?: ChatSource[];
};

type ConversationHistoryItem = {
  title?: string;
  conversation_id?: string | number;
};

type ModelOption = {
  id: number;
  displayName: string;
  value: string;
};

const API_BASE_URL = `${(process.env.REACT_APP_API_URL || "").replace(/\/$/, "")}/api`;

const ChatButton = () => {
  const location = useLocation();
  const [companies, setCompanies] = useState<string[]>([]);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputMessage, setInputMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState("all");
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [activeTab] = useState("documents");
  const [isSettingsPanelOpen, setIsSettingsPanelOpen] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState("");
  const [selectedModel, setSelectedModel] = useState("");
  const [isChatExpanded, setIsChatExpanded] = useState(false);
  const [newChatTrigger, setNewChatTrigger] = useState(0);
  const [conversationHistory, setConversationHistory] = useState<ConversationHistoryItem[]>([]);
  const [isHistoryLoading, setIsHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState("");
  const [selectedConversationId, setSelectedConversationId] = useState<string | number | null>(null);

  const messagesEndRef = useRef<HTMLDivElement | null>(null);

  const providers = ["anthropic"];
  const models: Record<string, ModelOption[]> = {
    anthropic: [
      { id: 1, displayName: "Opus 4.7(latest)", value: "claude-opus-4-7" },
      { id: 2, displayName: "Opus 4.6", value: "claude-opus-4-6" },
      { id: 3, displayName: "Sonnet", value: "claude-sonnet-4-6" },
      { id: 4, displayName: "Haiku", value: "claude-haiku-4-5-20251001" },
    ],
  };

  useEffect(() => {
    if (isChatOpen && activeTab === "documents") {
      fetchCompanies();
    }
  }, [isChatOpen, activeTab]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, activeTab]);

  useEffect(() => {
    setIsChatOpen(false);
    setIsChatExpanded(false);
    setIsSettingsPanelOpen(false);
  }, [location.pathname, location.search, location.hash]);

  const closeSettingsPanel = () => {
    setIsSettingsPanelOpen(false);
  };

  const fetchConversationHistory = async () => {
    setIsHistoryLoading(true);
    setHistoryError("");

    try {
      const response = await axios.get(`${API_BASE_URL}/nlp/conversations`, {
        withCredentials: true,
        params: {
          skip: 0,
          limit: 15,
          status: "active",
        },
      });

      const conversations = Array.isArray(response?.data?.conversations)
        ? (response.data.conversations as ConversationHistoryItem[])
        : [];

      setConversationHistory(conversations);
    } catch (error) {
      console.error("[ChatButton] Error fetching conversation history:", error);
      setConversationHistory([]);
      setHistoryError("Unable to load chat history.");
    } finally {
      setIsHistoryLoading(false);
    }
  };

  const handleSelectConversation = (conversationId: string | number | null | undefined) => {
    if (conversationId === undefined || conversationId === null) {
      return;
    }
    setSelectedConversationId(conversationId);
    setIsSettingsPanelOpen(false);
  };

  const handleNewChat = () => {
    setMessages([]);
    setInputMessage("");
    setIsLoading(false);
    setSelectedCompany("all");
    setSelectedConversationId(null);
    setNewChatTrigger((prev) => prev + 1);
    setIsSettingsPanelOpen(false);
  };

  useEffect(() => {
    if (isSettingsPanelOpen) {
      fetchConversationHistory();
    }
  }, [isSettingsPanelOpen]);

  const fetchCompanies = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/rag/companies`);
      const companyList = Array.isArray(response.data?.companies)
        ? (response.data.companies as string[])
        : [];
      setCompanies(companyList);
    } catch (error: any) {
      console.error(
        "[ChatButton] Error fetching companies:",
        error?.message,
        error?.response?.status,
        error?.response?.data,
      );
    }
  };

  const handleSendMessage = async () => {
    if (!inputMessage.trim()) return;

    const userMessage: ChatMessage = { type: "user", text: inputMessage };
    setMessages((prev) => [...prev, userMessage]);
    setInputMessage("");
    setIsLoading(true);

    try {
      const response = await axios.post(`${API_BASE_URL}/chat`, {
        query: inputMessage,
        mode: "rag",
        company: selectedCompany,
      });

      const assistantMessage: ChatMessage = {
        type: "assistant",
        text: response.data?.answer || "No response available.",
        sources: Array.isArray(response.data?.sources) ? response.data.sources : [],
      };

      setMessages((prev) => [...prev, assistantMessage]);
    } catch (error: any) {
      console.error(
        "[ChatButton] Error sending message:",
        error?.message,
        error?.response?.status,
        error?.response?.data,
      );
      const errorMessage: ChatMessage = {
        type: "assistant",
        text: "Sorry, I encountered an error processing your request. Please try again.",
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <>
      <button
        className="group fixed bottom-2 right-6 z-[999] inline-flex h-12 w-12 items-center justify-center overflow-hidden rounded-full border border-white/40 bg-gradient-to-r from-emerald-500 to-sky-500 px-0 shadow-[0_8px_18px_rgba(16,24,40,0.14)] transition-all duration-300 hover:w-[380px] hover:justify-start hover:px-4 hover:from-emerald-600 hover:to-sky-600 hover:shadow-[0_12px_24px_rgba(16,24,40,0.18)]"
        onClick={() => {
          if (isChatOpen) {
            setIsChatOpen(false);
            setIsChatExpanded(false);
            return;
          }
          setIsChatOpen(true);
        }}
        title="Open AI Chat"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="white"
          strokeWidth="2"
          className="shrink-0 transition-transform duration-300 group-hover:scale-100"
        >
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
        </svg>
        <span className="ml-0 max-w-0 -translate-x-2 overflow-hidden whitespace-nowrap text-sm font-semibold text-white opacity-0 transition-all duration-300 group-hover:ml-3 group-hover:max-w-[320px] group-hover:translate-x-0 group-hover:opacity-100">
          Talk to our Chat Assistant for additional Insights
        </span>
        {messages.length > 0 && activeTab === "documents" && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full min-w-[20px] text-center">
            {messages.length}
          </span>
        )}
      </button>

      <div
        className={`fixed bottom-[78px] right-6 bg-white/96 rounded-2xl shadow-[0_12px_30px_rgba(16,24,40,0.14)] flex flex-col z-[1000] overflow-hidden border border-emerald-100/70 backdrop-blur transition-all duration-200 ${
          isChatExpanded ? "w-[min(720px,calc(100vw-32px))]" : "w-[380px]"
        } ${isChatOpen ? "animate-slideUp" : "invisible pointer-events-none opacity-0"}`}
        style={{
          maxHeight: isChatExpanded
            ? "calc(100vh - 90px)"
            : "calc(100vh - 160px)",
          height: isChatExpanded ? "min(760px,calc(100vh-90px))" : "520px",
        }}
      >
        <div className="bg-gradient-to-r from-emerald-500 to-sky-500 text-white px-4 py-2.5 flex justify-between items-center shrink-0 border-b border-white/35">
          <h3 className="text-sm font-semibold m-0 flex items-center gap-1.5 tracking-wide">🤖 Benchmarking Assistant</h3>
          <div className="flex items-center gap-2">
            <button
              className="bg-white/10 border border-white/20 text-white cursor-pointer p-1 w-6 h-6 flex items-center justify-center rounded hover:bg-white/25 transition-colors"
              onClick={() => {
                if (isSettingsPanelOpen) {
                  closeSettingsPanel();
                  return;
                }
                setIsSettingsPanelOpen(true);
              }}
              title="AI Model Settings"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <circle cx="12" cy="12" r="3"></circle>
                <path d="M19.4 15a1.7 1.7 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.7 1.7 0 0 0-1.82-.33 1.7 1.7 0 0 0-1 1.54V21a2 2 0 0 1-4 0v-.09a1.7 1.7 0 0 0-1-1.54 1.7 1.7 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.7 1.7 0 0 0 .33-1.82 1.7 1.7 0 0 0-1.54-1H3a2 2 0 0 1 0-4h.09a1.7 1.7 0 0 0 1.54-1 1.7 1.7 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.7 1.7 0 0 0 1.82.33h.01a1.7 1.7 0 0 0 1-1.54V3a2 2 0 0 1 4 0v.09a1.7 1.7 0 0 0 1 1.54h.01a1.7 1.7 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.7 1.7 0 0 0-.33 1.82v.01a1.7 1.7 0 0 0 1.54 1H21a2 2 0 0 1 0 4h-.09a1.7 1.7 0 0 0-1.54 1Z"></path>
              </svg>
            </button>
            <button
              className="bg-white/10 border border-white/20 text-white text-lg cursor-pointer p-0 w-6 h-6 flex items-center justify-center rounded hover:bg-white/25 transition-colors"
              onClick={() => setIsChatExpanded((prev) => !prev)}
              title={isChatExpanded ? "Restore size" : "Increase size"}
            >
              {isChatExpanded ? (
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <polyline points="14 10 14 4 20 4"></polyline>
                  <polyline points="10 14 10 20 4 20"></polyline>
                  <line x1="14" y1="4" x2="21" y2="11"></line>
                  <line x1="3" y1="13" x2="10" y2="20"></line>
                </svg>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <polyline points="15 3 21 3 21 9"></polyline>
                  <polyline points="9 21 3 21 3 15"></polyline>
                  <line x1="21" y1="3" x2="14" y2="10"></line>
                  <line x1="3" y1="21" x2="10" y2="14"></line>
                </svg>
              )}
            </button>
            <button
              className="bg-white/10 border border-white/20 text-white text-lg cursor-pointer p-0 w-6 h-6 flex items-center justify-center rounded hover:bg-white/25 transition-colors"
              onClick={() => {
                setIsChatOpen(false);
                setIsChatExpanded(false);
              }}
              title="Close chat"
            >
              ✕
            </button>
          </div>
        </div>

        {isSettingsPanelOpen && (
          <div
            className={`absolute left-0 top-0 bottom-0 bg-white border-r border-emerald-200 shadow-lg rounded-tl-2xl rounded-bl-2xl animate-slideInLeft z-50 ${
              isChatExpanded ? "w-72" : "w-48"
            }`}
          >
            <div className="p-4 h-full flex flex-col">
              <button className="absolute top-2 right-2 text-gray-400 hover:text-gray-600 transition-colors" onClick={closeSettingsPanel} title="Close settings">✕</button>
              <div className="space-y-4 flex-1 overflow-y-auto">
                <div className="mt-6">
                  <label className="text-xs font-semibold text-gray-700 block mb-2">Provider</label>
                  <select
                    value={selectedProvider}
                    onChange={(event) => {
                      const provider = event.target.value;
                      setSelectedProvider(provider);
                      setSelectedModel("");
                    }}
                    className="w-full px-3 py-2 border border-emerald-200 rounded-lg text-xs bg-white cursor-pointer focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  >
                    <option value="">Select provider</option>
                    {providers.map((provider) => (
                      <option key={provider} value={provider}>
                        {provider.charAt(0).toUpperCase() + provider.slice(1)}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="text-xs font-semibold text-gray-700 block mb-2">Model</label>
                  <select
                    value={selectedModel}
                    onChange={(event) => {
                      setSelectedModel(event.target.value);
                      if (selectedProvider && event.target.value) {
                        localStorage.setItem(
                          "aiModelSettings",
                          JSON.stringify({
                            provider: selectedProvider,
                            model: event.target.value,
                          }),
                        );
                        setIsSettingsPanelOpen(false);
                      }
                    }}
                    disabled={!selectedProvider}
                    className="w-full px-3 py-2 border border-emerald-200 rounded-lg text-xs bg-white cursor-pointer focus:outline-none focus:ring-2 focus:ring-emerald-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  >
                    <option value="">Select model</option>
                    {(models[selectedProvider] || []).map((model) => (
                      <option key={model.id} value={model.value}>
                        {model.displayName}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <button className="w-full px-3 py-2.5 bg-slate-100 text-slate-700 border border-slate-200 rounded-xl text-[15px] font-medium cursor-pointer transition-colors hover:bg-slate-200 flex items-center gap-3" onClick={handleNewChat} title="Start a new chat">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="text-slate-600">
                      <path d="M3 21h6" />
                      <path d="M14.5 4.5a2.12 2.12 0 1 1 3 3L8 17l-4 1 1-4 9.5-9.5z" />
                    </svg>
                    <span>New chat</span>
                  </button>
                </div>

                <div className="rounded-2xl bg-white border border-slate-200 p-3">
                  <div className="text-xs font-semibold text-gray-800 mb-3">Chats</div>
                  <div className="max-h-64 overflow-y-auto space-y-1.5 pr-1">
                    {isHistoryLoading && <div className="text-[11px] text-gray-500 px-2 py-1">Loading history...</div>}
                    {!isHistoryLoading && historyError && <div className="text-[11px] text-red-500 px-2 py-1">{historyError}</div>}
                    {!isHistoryLoading && !historyError && conversationHistory.length === 0 && (
                      <div className="text-[11px] text-gray-500 px-2 py-1">No active conversations.</div>
                    )}
                    {!isHistoryLoading && !historyError && conversationHistory.map((conversation) => {
                      const title = conversation?.title || "Untitled Chat";
                      const conversationId = conversation?.conversation_id;
                      const isSelected = selectedConversationId === conversationId;
                      return (
                        <button
                          key={String(conversationId)}
                          type="button"
                          onClick={() => handleSelectConversation(conversationId)}
                          className={`w-full text-left rounded-2xl px-4 py-2.5 text-xs leading-4 transition-colors ${
                            isSelected
                              ? "bg-blue-200/80 text-blue-900 font-semibold"
                              : "text-gray-700 hover:bg-slate-200/80"
                          }`}
                          title={title}
                        >
                          <span className="block whitespace-normal break-words">{title}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === "DataQuery" ? (
          <div className="flex flex-col flex-1 overflow-hidden">
            <div className="px-3 py-2 border-b border-emerald-100 bg-emerald-50/40">
              <label htmlFor="company-select" className="text-sm text-gray-600 mr-2.5 font-medium">Search in:</label>
              <select
                id="company-select"
                className="px-2.5 py-1.5 border border-emerald-200 rounded-xl text-sm bg-white cursor-pointer focus:outline-none focus:ring-2 focus:ring-emerald-500"
                value={selectedCompany}
                onChange={(event) => setSelectedCompany(event.target.value)}
              >
                <option value="all">All Companies</option>
                {companies.map((company, index) => (
                  <option key={index} value={company}>{company}</option>
                ))}
              </select>
            </div>

            <div className="flex-1 overflow-y-auto px-3 py-3 bg-white min-h-0">
              {messages.length === 0 ? (
                <div className="text-center py-6 px-4">
                  <div className="text-4xl mb-3">💬</div>
                  <div className="text-sm font-semibold text-emerald-900 mb-1">Start a conversation!</div>
                  <div className="text-xs text-gray-500">Ask about KPIs, benchmarks, or data in your documents.</div>
                </div>
              ) : (
                <>
                  {messages.map((message, index) => (
                    <div key={index} className="mb-4">
                      <div className={`${message.type === "user" ? "ml-auto bg-gradient-to-r from-emerald-500 to-sky-500 text-white rounded-[18px_18px_4px_18px]" : "bg-slate-50/80 text-gray-600 rounded-[18px_18px_18px_4px] border border-slate-200/80"} p-3 max-w-[80%] ${message.type === "user" ? "float-right clear-both" : ""}`}>
                        <div className="leading-6 text-sm whitespace-pre-wrap">{message.text}</div>
                        {Array.isArray(message.sources) && message.sources.length > 0 && (
                          <div className="mt-3 pt-3 border-t border-black/10">
                            <div className="text-xs font-semibold mb-1.5 text-gray-600">📌 Sources:</div>
                            {message.sources.slice(0, 3).map((source, idx) => (
                              <div key={idx} className="text-xs text-gray-600 py-1">
                                {source.filename} (similarity: {(((source.similarity || 0) as number) * 100).toFixed(1)}%)
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                  {isLoading && (
                    <div className="bg-gray-100 text-gray-800 rounded-[18px_18px_18px_4px] p-3 max-w-[80%]">
                      <div className="flex items-center p-3">
                        <div className="flex gap-1">
                          <div className="w-2 h-2 bg-gray-600 rounded-full animate-bounce" style={{ animationDelay: "-0.32s" }}></div>
                          <div className="w-2 h-2 bg-gray-600 rounded-full animate-bounce" style={{ animationDelay: "-0.16s" }}></div>
                          <div className="w-2 h-2 bg-gray-600 rounded-full animate-bounce"></div>
                        </div>
                      </div>
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </>
              )}
            </div>

            <div className="px-3 py-2.5 border-t border-emerald-100/70 bg-emerald-50/30 flex gap-2 flex-shrink-0">
              <input
                type="text"
                className="flex-1 px-3 py-2 border border-emerald-200/90 rounded-full text-xs outline-none focus:border-emerald-500 bg-white/95 transition-colors"
                placeholder="Ask about your documents..."
                value={inputMessage}
                onChange={(event) => setInputMessage(event.target.value)}
                onKeyPress={handleKeyPress}
                disabled={isLoading}
              />
              <button
                className="px-3 py-2 bg-gradient-to-r from-emerald-500 to-sky-500 text-white border-none rounded-full text-xs font-semibold cursor-pointer flex items-center gap-1 transition-all hover:from-emerald-600 hover:to-sky-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
                onClick={handleSendMessage}
                disabled={isLoading || !inputMessage.trim()}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="22" y1="2" x2="11" y2="13"></line>
                  <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
                </svg>
                Send
              </button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col flex-1 min-h-0 overflow-hidden">
            <SqlChatWidget
              selectedModel={selectedModel}
              selectedProvider={selectedProvider}
              newChatTrigger={newChatTrigger}
              selectedConversationId={selectedConversationId}
            />
          </div>
        )}
      </div>
    </>
  );
};

export default ChatButton;